import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ChatProfileSection = "reservations" | "rooms";

export type ChatProfileSectionsVisibility = Record<ChatProfileSection, boolean>;

const STORAGE_KEY = "chats:profileSections";

const DEFAULT_VISIBILITY: ChatProfileSectionsVisibility = {
  reservations: true,
  rooms: true,
};

const readStoredVisibility = (): ChatProfileSectionsVisibility => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    if (!stored) return DEFAULT_VISIBILITY;

    const parsed = JSON.parse(stored) as Partial<ChatProfileSectionsVisibility>;

    return {
      reservations: parsed.reservations ?? DEFAULT_VISIBILITY.reservations,
      rooms: parsed.rooms ?? DEFAULT_VISIBILITY.rooms,
    };
  } catch (error) {
    console.warn("Failed to read chat profile section preferences:", error);
    return DEFAULT_VISIBILITY;
  }
};

interface ChatProfileSectionsContextType {
  visibility: ChatProfileSectionsVisibility;
  isSectionVisible: (section: ChatProfileSection) => boolean;
  toggleSection: (section: ChatProfileSection) => void;
}

const ChatProfileSectionsContext = createContext<
  ChatProfileSectionsContextType | undefined
>(undefined);

interface ChatProfileSectionsProviderProps {
  children: ReactNode;
}

export const ChatProfileSectionsProvider = ({
  children,
}: ChatProfileSectionsProviderProps) => {
  const [visibility, setVisibility] =
    useState<ChatProfileSectionsVisibility>(readStoredVisibility);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
    } catch (error) {
      console.warn(
        "Failed to persist chat profile section preferences:",
        error,
      );
    }
  }, [visibility]);

  const toggleSection = useCallback((section: ChatProfileSection) => {
    setVisibility((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  const isSectionVisible = useCallback(
    (section: ChatProfileSection) => visibility[section],
    [visibility],
  );

  const value = useMemo(
    () => ({ visibility, isSectionVisible, toggleSection }),
    [visibility, isSectionVisible, toggleSection],
  );

  return (
    <ChatProfileSectionsContext.Provider value={value}>
      {children}
    </ChatProfileSectionsContext.Provider>
  );
};

export const useChatProfileSections = (): ChatProfileSectionsContextType => {
  const context = useContext(ChatProfileSectionsContext);

  if (!context) {
    throw new Error(
      "useChatProfileSections must be used within a ChatProfileSectionsProvider",
    );
  }

  return context;
};
